// HR › Payroll period review (UI-3A on the CD-S2 / Batch 6A review). The server builds every figure from the facts
// (attendance, approved leave, approved overtime, compensation, deductions, contributions) and decides what blocks
// sending to Finance; this view puts the next step first, attaches each blocker to the way out of it, and explains
// each employee's pay behind a disclosure.
import { BasicPayWorking, CalculationCheck, NetPayEquation } from "../payslips/PayCalculation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronDown, Lock, RotateCcw } from "lucide-react";

import { businessApi } from "../../../../services/api";
import { HOLIDAY_SCOPE_NOTE } from "../holidayPayView";
import PayrollHolidayLines, { HolidayReviewDialog } from "./PayrollHolidayLines";
import PayrollPremiumLines, { PREMIUM_LABEL, PremiumReviewDialog } from "./PayrollPremiumLines";
import { ContributionGlance, PayrollContributions, PayrollWithholding, WithholdingGlance } from "./StatutoryContributions";
import { AGENCY_NAME } from "../contributionsView";
import { Badge, Button, DisclosureLink, ErrorState, Money, formatMoney, Notice, SearchInput, Select, SkeletonRows, Status, Toolbar, cn, focusRing } from "../../../../components/ui";
import PeriodActionDialog from "../payroll/PeriodActionDialog";
import CalculatePayrollDialog from "../payroll/CalculatePayrollDialog";
import { actionError, shortDate } from "../payroll/hrPayroll";

const minutes = (value) => {
  const total = Number(value || 0);
  if (!total) return "0 min";
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return hours ? `${hours} h${rest ? ` ${rest} min` : ""}` : `${rest} min`;
};
const day = (value) => (value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })
  : "—");
/**
 * Days worked as the payslip counts them (Payroll Gate 1, APP-HR-097): the server sends the number of days with
 * attendance and how many of those were half days; a half day is half a day, so 8 full days and 1 half day read
 * "8.5 days worked", never "9".
 */
export function daysWorked(attendance = {}) {
  return Number(attendance.workedDays || 0) - Number(attendance.halfDays || 0) * 0.5;
}
export function daysWorkedText(attendance = {}) {
  const days = daysWorked(attendance);
  const halves = Number(attendance.halfDays || 0);
  return `${days} ${days === 1 ? "day" : "days"} worked${halves ? ` (${halves} half ${halves === 1 ? "day" : "days"})` : ""}`;
}

// Salary basis: how the pay is stated (Compensation), not how often payroll runs (the pay schedule).
const FREQUENCY = { monthly: "Per month", semi_monthly: "Per half-month", bi_weekly: "Per two weeks", weekly: "Per week", daily: "Per day", hourly: "Per hour" };

/** What HR does now, by period status. Returned payroll leads with Finance's reason. */
function currentStep(status, lastReview) {
  switch (status) {
    case "draft":
      return { tone: "neutral", title: "Not started", text: "Start preparing to check each employee's facts and calculate this payroll." };
    case "collecting_inputs":
      return lastReview?.action === "returned"
        ? { tone: "warning", title: "Returned by Finance", text: `Returned by Finance${lastReview.reviewedByName ? ` (${lastReview.reviewedByName})` : ""}${lastReview.remarks ? `: ${String(lastReview.remarks).trim().replace(/[.!?]+$/, "")}` : ""}. Correct the facts, recalculate, then send it again.` }
        : { tone: "neutral", title: "Preparing", text: "Check each employee below, resolve anything flagged, then calculate." };
    case "computed":
      return lastReview?.action === "returned"
        ? { tone: "warning", title: "Recalculated after a return", text: `Finance returned this payroll${lastReview.reviewedByName ? ` (${lastReview.reviewedByName})` : ""}${lastReview.remarks ? `: ${String(lastReview.remarks).trim().replace(/[.!?]+$/, "")}` : ""}. Check the results, then send it again.` }
        : { tone: "neutral", title: "Calculated", text: "Check the results. Send to Finance once nothing is listed below." };
    case "under_review":
      return { tone: "lock", title: "Waiting for Finance", text: "With Finance for review. Attendance, leave, overtime and pay for these dates are locked until Finance approves or returns the payroll." };
    case "approved":
      return { tone: "success", title: "Approved by Finance — ready to release payslips", text: "Releasing lets employees see their payslips. It does not pay anyone: Finance then pays each salary." };
    case "released":
      return { tone: "neutral", title: "Released", text: "Employees can see their payslips. Finance records each salary payment and employees confirm receipt in My HR." };
    case "settled":
      return { tone: "success", title: "Settled", text: "Every salary in this payroll was paid and confirmed." };
    case "cancelled":
      return { tone: "neutral", title: "Cancelled", text: "Its dates can be used by a new payroll." };
    default:
      return { tone: "neutral", title: "", text: "" };
  }
}

// A review list (DESIGN.md › Data collections): name search and the issue filter are there whenever there is more than
// one employee to find; choosing an order is only offered once the payroll is longer than a screenful. It grows 50 at
// a time.
const ORDER_CONTROL_FROM = 8;
const LIST_STEP = 50;

// Once a payroll is with Finance, approved, released or settled, its payslips are the record. The review is still
// worked out from today's records, so those figures are a reference beside the payslips, never shown in their place.
const RECORD_STATUSES = ["under_review", "approved", "released", "settled"];

const STEP_ICON = { warning: AlertTriangle, success: CheckCircle2, lock: Lock };
const STEP_ICON_CLASS = { warning: "text-warning", success: "text-success", lock: "text-secondary" };

/** Blockers in the operator's words where the server's wording is a mechanism. */
function blockerMessage(item, period) {
  if (item.code === "period_not_ended" && period?.end_date) {
    const end = new Date(`${String(period.end_date).slice(0, 10)}T00:00:00`);
    const next = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    return `The period ends ${shortDate(end)}. You can send it to Finance from ${shortDate(next)}.`;
  }
  if (item.code === "results_stale") return "Employee facts changed after the last calculation. The employees marked \u201cChanged since calculation\u201d below show what changed and where; recalculate to keep the new figures.";
  return item.message;
}

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-meta text-secondary">{label}</dt>
      <dd className="text-body-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

// One section of an opened employee. Stacked with hairlines on narrow screens; from 1280 px the sections flow into
// two columns the browser balances, so neither side is left hanging whatever mix of sections an employee has.
const SECTION = "border-t border-subtle py-4 first:border-t-0 xl:mb-6 xl:border-t-0 xl:py-0";
// Kept whole in a column. The statutory section is the exception: it may split between contributions and withholding.
const WHOLE = "xl:break-inside-avoid";

// The collapsed row's columns from 1280 px: employee, contributions and tax, review state, net pay, chevron. The
// list's head row uses the same template so its labels sit over their columns. The breakpoints follow the width the
// content has beside the business sidebar, not the window: stacked below 1024 px, three columns to 1280 px.
const ROW_COLUMNS = "xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_11rem_8rem_1rem]";

/** A label and its amount on one line of a pay statement; `total` is the ruled, heavier closing line. */
function Line({ label, value, total = false }) {
  return (
    <div className={cn("flex justify-between gap-3", total && "border-t border-subtle pt-1 font-semibold text-foreground")}>
      <dt className={total ? undefined : "text-secondary"}>{label}</dt>
      <dd><Money value={value} /></dd>
    </div>
  );
}

const ALERT_TONE = { blocker: "danger", warning: "warning", info: "neutral" };

/** CD-S3: the withholding tax actually taken on this payslip (calculated or reviewed), else 0. */
const withheld = (member) => {
  const line = member.statutory?.withholding;
  return line && (line.state === "AUTO_CALCULATED" || line.state === "MANUALLY_REVIEWED") ? Number(line.employeeCents || 0) : 0;
};

/** A link to the record a fact comes from; a plain anchor outside a router (tests of the bare component). */
function SourceLink({ to, children, className }) {
  const inRouter = useInRouterContext();
  const style = cn("inline-block rounded-control font-medium text-accent hover:underline", focusRing, className);
  return inRouter ? <Link to={to} className={style}>{children}</Link> : <a href={to} className={style}>{children}</a>;
}

/** Where HR records what happened on a day with no attendance (ATT-1: counted absent unless corrected). */
function AttendanceLink() {
  return <SourceLink to="/business/hr/attendance" className="ml-5">Open Attendance</SourceLink>;
}

/**
 * Payroll Gate 3: where each changed component comes from (the server names the component; this only says where its
 * source record lives). Two changes that point to the same place share one link.
 */
const CHANGE_SOURCE = {
  "days worked": ["attendance", "Attendance"], "time worked": ["attendance", "Attendance"], absences: ["attendance", "Attendance"],
  overtime: ["attendance", "Attendance"], "overtime pay": ["attendance", "Attendance"], "unpaid days": ["attendance", "Attendance"],
  "paid leave": ["leave", "Leave"], "unpaid leave": ["leave", "Leave"],
  "base pay": ["pay", "Pay"], allowance: ["pay", "Pay"], "pay record": ["pay", "Pay"],
  "not in the last calculation": ["pay", "Pay schedule"], "no longer paid by this payroll": ["pay", "Pay schedule"],
  deductions: ["deductions", "Deductions"], "total deductions": ["deductions", "Deductions"],
};
export function changeSources(memberId, changes = []) {
  const seen = new Map();
  for (const word of changes) {
    const source = CHANGE_SOURCE[word];
    if (source && !seen.has(source[0])) seen.set(source[0], { to: `/business/hr/employees/${memberId}/${source[0]}`, label: source[1] });
  }
  return [...seen.values()];
}

/** In a record: whether today's records would no longer give this employee's payslip (or they have none). */
const recordDiffers = (member) => Boolean(member.changes?.length) || !member.stored;

/** What a member's row still needs, for the badge, the filter and the order (blockers first). */
export function memberState(member) {
  // "Changed since calculation" is its own state (recalculate), not one more thing to resolve by hand.
  const blockers = member.warnings.filter((item) => item.level === "blocker" && item.code !== "changed_since_calculation").length;
  const warnings = member.warnings.filter((item) => item.level === "warning").length;
  const changed = Boolean(member.changes?.length);
  return { blockers, warnings, changed, attention: blockers > 0 || warnings > 0 || changed, rank: blockers ? 0 : changed ? 1 : warnings ? 2 : 3 };
}

// Contribution states and their reasons are shown once, in the contributions table, not again as alerts.
const shownInContributions = (item) => /^statutory_(review|blocked|number_missing)/.test(item.code);

function EmployeeCard({ member, periodId, status, onChanged, focus, canManage, holidayLines, onReviewHoliday, onReviewPremium, record = false }) {
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const card = useRef(null);
  const focusAgency = focus?.memberId === member.memberId ? focus.agency : "";
  useEffect(() => {
    if (focus?.memberId !== member.memberId) return;
    setOpen(true);
    card.current?.scrollIntoView?.({ block: "nearest" });
  }, [focus, member.memberId]);
  // A holiday blocker opens the employee and brings that day into view.
  useEffect(() => {
    if (!open || focus?.memberId !== member.memberId || !focus.holidayDate) return;
    document.getElementById(`holiday-${member.memberId}-${focus.holidayDate}`)?.scrollIntoView?.({ block: "center" });
  }, [open, focus, member.memberId]);
  const alerts = member.warnings.filter((item) => !shownInContributions(item) && item.code !== "changed_since_calculation");
  const { blockers, warnings, changed } = memberState(member);
  const a = member.attendance;
  const detailId = `employee-${member.memberId}-detail`;
  const workingId = `employee-${member.memberId}-working`;
  const sources = changeSources(member.memberId, member.changes);
  // On a payroll that is a record, the payslip is the figure. Today's working is shown beside it, and kept closed
  // when it no longer matches, so a recalculation is never read as what the employee was paid.
  const payslip = record ? member.stored : null;
  const differs = record && (changed || !payslip);
  const taxWithheld = withheld(member);

  // Built only when shown: a long payroll renders many closed cards.
  const needsAttention = !record && (alerts.length || changed);
  const breakdown = () => (
    <div>
        {needsAttention ? (
          <section aria-label={`What needs attention for ${member.name}`} className="space-y-2 border-b border-subtle py-4">
            <h4 className="text-label text-foreground">Needs attention</h4>
            {changed ? (
              <div className="space-y-1 text-body-sm">
                <Status tone="warning" icon>Facts changed after the last calculation: {member.changes.join(", ")}.</Status>
                {member.stored ? (
                  <p className="ml-5 text-secondary">
                    Calculated net pay <Money value={member.stored.netPayCents} className="font-medium text-foreground" />
                    {" "}→ from today's facts <Money value={member.netPayCents} className="font-medium text-foreground" />.
                    {" "}Recalculate to keep the new figures; the review below already shows them.
                  </p>
                ) : <p className="ml-5 text-secondary">Recalculate to include this employee.</p>}
                {sources.length ? (
                  <p className="ml-5 flex flex-wrap gap-x-4 gap-y-1">
                    {sources.map((source) => <SourceLink key={source.to} to={source.to}>Open {source.label}</SourceLink>)}
                  </p>
                ) : null}
              </div>
            ) : null}
            {alerts.length ? (
              <ul className="space-y-1.5">
                {alerts.map((item) => (
                  <li key={item.code} className="text-body-sm">
                    <Status tone={ALERT_TONE[item.level] || "neutral"} icon={item.level !== "info"}>{item.message}</Status>
                    {item.code === "presumed_absent" || item.code === "manual_leave_or_holiday" ? <AttendanceLink /> : null}
                    {item.code === "no_pay_schedule" ? <SourceLink to={`/business/hr/employees/${member.memberId}/pay`} className="ml-5">Choose a pay schedule</SourceLink> : null}
                    {item.code === "weekly_rest_missing" || item.code === "schedule_missing_days" ? <SourceLink to="/business/hr/shifts" className="ml-5">Open HR › Shifts</SourceLink> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      <div className="xl:columns-2 xl:gap-x-12 xl:pt-4 xl:[column-rule-color:theme(borderColor.subtle)] xl:[column-rule-style:solid] xl:[column-rule-width:1px]">
        {member.pay && member.calculation ? (
          <section aria-label={`How the basic pay was worked out for ${member.name}`} className={cn(SECTION, WHOLE)}>
            <h4 className="text-label text-foreground">
              How the basic pay was worked out
              {member.calculation.basic.payBasisLabel ? <span className="font-normal text-secondary"> · {member.calculation.basic.payBasisLabel}</span> : null}
            </h4>
            <BasicPayWorking calculation={member.calculation} caption={`How the basic pay was worked out for ${member.name}`} />
            <NetPayEquation calculation={member.calculation} className="mt-3" />
            <CalculationCheck calculation={member.calculation} />
          </section>
        ) : null}
        {member.pay ? (
          <section aria-label={`Earnings and deductions for ${member.name}`} className={cn(SECTION, WHOLE, "grid gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2")}>
            <section>
              <h4 className="mb-1 text-label text-foreground">Earnings</h4>
              <dl className="space-y-1 text-body-sm">
                {member.calculation
                  // Report 54A (D1): the server's own earning lines (named allowances, each holiday), in its order.
                  ? member.calculation.earnings.lines.map((line, index) => <Line key={`${line.code}-${index}`} label={line.label} value={line.amountCents} />)
                  : [
                    ["Base pay for the period", member.pay.baseSalaryCents + member.pay.leaveDeductionCents],
                    ["Less absences and unpaid leave", -(member.pay.leaveDeductionCents - Number(member.pay.suspensionDeductionCents || 0))],
                    ...(Number(member.pay.suspensionDeductionCents || 0) > 0
                      ? [[member.suspension?.unpaidDays === 1 ? "Less 1 suspended day" : `Less ${member.suspension?.unpaidDays ?? ""} suspended days`, -member.pay.suspensionDeductionCents]]
                      : []),
                    ["Allowances", member.pay.allowancesCents],
                    ["Approved overtime", member.pay.overtimePayCents],
                    ["Holiday pay", member.pay.holidayPayCents],
                  ].filter(([, value]) => value !== 0).map(([label, value]) => <Line key={label} label={label} value={value} />)}
                <Line total label="Gross" value={member.grossPayCents} />
              </dl>
            </section>
            <section>
              <h4 className="mb-1 text-label text-foreground">Deductions</h4>
              {member.deductions.length || member.pay.statutoryEmployeeCents || taxWithheld ? (
                <dl className="space-y-1 text-body-sm">
                  {member.calculation
                    ? member.calculation.deductions.lines.filter((line) => ["government", "tax"].includes(line.group) && !line.pending && line.amountCents > 0)
                      .map((line) => <Line key={line.code} label={line.label} value={line.amountCents} />)
                    : (
                      <>
                        {member.pay.statutoryEmployeeCents ? <Line label="Government contributions (employee share)" value={member.pay.statutoryEmployeeCents} /> : null}
                        {taxWithheld ? <Line label="Withholding tax" value={taxWithheld} /> : null}
                      </>
                    )}
                  {member.deductions.map((item) => (
                    <div key={`${item.code}-${item.employeeDeductionId}`}>
                      <Line label={item.name} value={item.amountCents} />
                      {item.remainingBalanceCents != null ? (
                        <div className="text-meta text-tertiary">
                          Installment of a balance of <Money value={item.remainingBalanceCents} />
                          {item.committedElsewhereCents ? <>, of which <Money value={item.committedElsewhereCents} /> is already withheld on another unpaid payslip</> : ""}
                          ; the balance goes down only when this salary is paid.
                        </div>
                      ) : null}
                    </div>
                  ))}
                  <Line total label="Total deductions" value={member.totalDeductionsCents} />
                </dl>
              ) : <p className="text-body-sm text-secondary">None.</p>}
              <div className="mt-2 flex justify-between gap-3 rounded-control bg-surface-sunken px-3 py-2 text-body-sm font-semibold text-foreground"><span>Net pay</span><Money value={member.netPayCents} /></div>
            </section>
          </section>
        ) : null}
        {member.pay && holidayLines?.length ? (
          <div className={cn(SECTION, WHOLE)}>
            <PayrollHolidayLines member={member} lines={holidayLines} status={status} canManage={canManage} onReview={(line) => onReviewHoliday(member, line)} />
          </div>
        ) : null}
        {member.pay && member.premiumLines?.length ? (
          <div className={cn(SECTION, WHOLE)}>
            <PayrollPremiumLines member={member} lines={member.premiumLines} status={status} canManage={canManage} onReview={(line) => onReviewPremium(member, line)} />
          </div>
        ) : null}
        <section aria-label={`Government contributions and withholding for ${member.name}`} className={cn(SECTION, "space-y-5")}>
          <PayrollContributions member={member} periodId={periodId} status={canManage ? status : "read_only"} onChanged={onChanged} focusAgency={focusAgency === "BIR_WHT" ? "" : focusAgency} />
          <PayrollWithholding member={member} periodId={periodId} status={canManage ? status : "read_only"} onChanged={onChanged} focus={focusAgency === "BIR_WHT"} />
        </section>
      <section aria-label={`Attendance and pay facts for ${member.name}`} className={cn(SECTION, WHOLE)}>
        <h4 className="mb-2 text-label text-foreground">Attendance and calculation facts</h4>
        <dl aria-label="Attendance and pay facts" className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4 xl:grid-cols-3">
          <Fact label="Counted dates">{day(member.employment.from)} – {day(member.employment.to)}</Fact>
          {member.calendar ? (
            <>
              <Fact label={member.calendar.source === "BUSINESS_DEFAULT_WEEK" ? "Working days (usual work week)" : "Working days (own schedule)"}>
                {member.employment.partial ? `${member.calendar.employedWorkDays} of ${member.calendar.periodWorkDays} while employed` : member.calendar.periodWorkDays}
              </Fact>
              <Fact label="Days due so far">{member.calendar.counts.daysDue}</Fact>
              {member.calendar.counts.daysNotYetDue ? <Fact label="Not due yet">{member.calendar.counts.daysNotYetDue}</Fact> : null}
              <Fact label="Rest days">{member.calendar.counts.restDays}{member.calendar.counts.restDaysWorked ? ` (${member.calendar.counts.restDaysWorked} worked)` : ""}</Fact>
            </>
          ) : <Fact label="Employed working days">{member.employment.employedWorkDays} of {member.employment.periodWorkDays}</Fact>}
          <Fact label="Salary basis">{member.compensation ? FREQUENCY[member.compensation.salaryFrequency] || member.compensation.salaryFrequency : "No compensation"}</Fact>
          <Fact label="Salary amount">{member.compensation ? <Money value={member.compensation.baseSalaryCents} /> : "—"}</Fact>
          <Fact label="Days worked">{daysWorked(a)}{Number(a.halfDays) ? ` (${a.halfDays} half ${Number(a.halfDays) === 1 ? "day" : "days"} counted as 0.5 each)` : ""}</Fact>
          <Fact label="Absent (recorded)">{a.absentDays}</Fact>
          {a.presumedAbsentDays ? <Fact label="No attendance (counted absent)">{a.presumedAbsentDays}</Fact> : null}
          {/* Report 57: lateness counts from the shift start once past the grace; what it cost is in the pay working. */}
          <Fact label="Late after the grace">{minutes(a.lateMinutes)}</Fact>
          {member.attendanceTime && (member.attendanceTime.unpaidLateMinutes || member.attendanceTime.unpaidUndertimeMinutes) ? (
            <Fact label="Unpaid late and early time">{minutes(member.attendanceTime.unpaidLateMinutes + member.attendanceTime.unpaidUndertimeMinutes)}{member.attendanceTime.lateCents + member.attendanceTime.undertimeCents > 0 ? <> · <Money value={member.attendanceTime.lateCents + member.attendanceTime.undertimeCents} /></> : null}</Fact>
          ) : null}
          {member.attendanceTime?.withinGraceMinutes ? <Fact label="Late within the grace (paid)">{minutes(member.attendanceTime.withinGraceMinutes)}</Fact> : null}
          <Fact label="Holidays in these dates">{member.holidays ? member.holidays.length : member.paidHolidays}</Fact>
          <Fact label="Extra time after shifts">{minutes(a.actualOvertimeMinutes)}</Fact>
          <Fact label="Approved overtime (paid)">{minutes(a.approvedOvertimeMinutes)}</Fact>
          <Fact label="Paid leave days">{member.leave.paidDays}</Fact>
          <Fact label="Unpaid leave days">{member.leave.unpaidDays}</Fact>
        </dl>
        <p className="mt-2 text-meta text-tertiary">The salary basis is how the pay is stated; how often it is paid comes from the pay schedule.</p>
      </section>
      </div>
    </div>
  );

  return (
    <li
      ref={card}
      className={cn(
        "scroll-mt-20 overflow-hidden rounded-panel border bg-surface transition-colors",
        open ? "border-strong" : "border-default"
      )}
    >
      <h3 className="m-0">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={detailId}
          className={cn(
            "grid w-full grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-hover",
            "lg:grid-cols-[minmax(0,1fr)_10rem_8.5rem_1rem] lg:gap-x-4",
            ROW_COLUMNS,
            open && "border-b border-subtle bg-surface-sunken",
            focusRing,
            "focus-visible:outline-offset-[-2px]"
          )}
        >
          <span className="col-start-1 row-start-1 min-w-0">
            <span className="block truncate text-body font-medium text-foreground">{member.name}</span>
            {record ? null : (
              <>
                <span className="mt-0.5 block text-meta text-secondary">
                  {daysWorkedText(a)} · overtime paid {minutes(a.approvedOvertimeMinutes)} of {minutes(a.actualOvertimeMinutes)} extra
                  {member.leave.unpaidDays ? ` · ${member.leave.unpaidDays} unpaid leave day(s)` : ""}
                </span>
                {member.suspension ? <span className="mt-0.5 block text-meta text-secondary">{member.suspension.label}</span> : null}
              </>
            )}
          </span>
          <span className="col-start-1 row-start-2 min-w-0 xl:col-start-2 xl:row-start-1">
            {record ? (
              <span className="block text-meta text-secondary">
                {payslip
                  ? <>Payslip · gross <Money value={payslip.grossPayCents} /> · deductions <Money value={payslip.totalDeductionsCents} /></>
                  : "No payslip in this payroll"}
              </span>
            ) : (
              <>
                <ContributionGlance statutory={member.statutory} />
                <WithholdingGlance line={member.statutory?.withholding} />
              </>
            )}
          </span>
          <span className="col-start-1 row-start-3 lg:col-start-2 lg:row-span-2 lg:row-start-1 xl:col-start-3 xl:row-span-1">
            {record ? (differs && payslip ? <Badge>Records changed since</Badge> : null)
              : blockers ? <Badge tone="danger">{blockers} to resolve</Badge>
                : changed ? <Badge tone="warning">Changed since calculation</Badge>
                  : warnings ? <Badge tone="warning">{warnings} to check</Badge> : null}
          </span>
          <span className="col-start-2 row-span-3 row-start-1 self-start text-right lg:col-start-3 lg:row-span-2 lg:self-center xl:col-start-4 xl:row-span-1">
            {record
              ? (payslip ? <Money value={payslip.netPayCents} className="text-money text-foreground" /> : <span className="text-body-sm text-secondary">—</span>)
              : member.pay
                ? <Money value={member.netPayCents} className="text-money text-foreground" />
                : <span className="text-body-sm text-secondary">Not paid</span>}
            <span className="block text-meta text-tertiary">net pay</span>
          </span>
          <ChevronDown aria-hidden="true" className={cn("col-start-3 row-start-1 mt-1 h-4 w-4 self-start text-tertiary transition-transform lg:col-start-4 lg:row-span-2 lg:mt-0 lg:self-center xl:col-start-5 xl:row-span-1", open && "rotate-180")} />
        </button>
      </h3>
      {open ? (
        <div id={detailId} className="px-4">
          {record ? (
            <section aria-label={`Payslip of ${member.name}`} className="space-y-2 py-4">
              <h4 className="text-label text-foreground">Payslip</h4>
              {payslip ? (
                <dl className="max-w-sm space-y-1 text-body-sm">
                  <Line label="Gross" value={payslip.grossPayCents} />
                  <Line label="Total deductions" value={payslip.totalDeductionsCents} />
                  <Line total label="Net pay" value={payslip.netPayCents} />
                </dl>
              ) : <p className="text-body-sm text-secondary">This payroll has no payslip for {member.name}. They were not part of it when it was calculated.</p>}
              {differs ? (
                <div className="max-w-[75ch] space-y-1 text-body-sm">
                  <Status icon>
                    {payslip
                      ? <>Worked out again from today's records, net pay would be <Money value={member.netPayCents} className="font-medium" />.</>
                      : <>Worked out from today's records, they would be paid <Money value={member.netPayCents} className="font-medium" />.</>}
                  </Status>
                  <p className="ml-5 text-secondary">
                    {payslip && member.changes?.length ? `What differs: ${member.changes.join(", ")}. ` : ""}
                    The payslip is the record and is not changed by this. Records or pay rules changed after this payroll was calculated.
                  </p>
                  <p className="ml-2">
                    <Button size="sm" variant="quiet" aria-expanded={working} aria-controls={workingId} onClick={() => setWorking((value) => !value)}>
                      {working ? "Hide the working from today's records" : "Show the working from today's records"}
                    </Button>
                  </p>
                </div>
              ) : <p className="text-meta text-secondary">The working below is from today's records and matches this payslip.</p>}
            </section>
          ) : null}
          {!differs || working ? (
            <div id={workingId} className={record ? "border-t border-subtle" : undefined}>
              {differs ? <p className="pt-3 text-meta text-secondary">From today's records, for reference. These are not the payslip's figures.</p> : null}
              {breakdown()}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * The totals of a payroll that is a record: its payslips as they were calculated, never a recalculation. `payslips`
 * are the period's result rows when the page has them; otherwise the stored figures the review carries per employee.
 * Returns null when nothing is stored.
 */
export function payslipTotals(review, payslips) {
  const fromRows = (Array.isArray(payslips) ? payslips : [])
    .filter((row) => String(row.payroll_result_storage_status || row.status || "").toLowerCase() !== "voided")
    .map((row) => ({
      grossPayCents: row.payroll_result_gross_pay_cents ?? row.gross_pay_cents,
      totalDeductionsCents: row.payroll_result_total_deductions_cents ?? row.total_deductions_cents,
      netPayCents: row.payroll_result_net_pay_cents ?? row.net_pay_cents,
    }));
  const rows = fromRows.length ? fromRows : review.members.map((member) => member.stored).filter(Boolean);
  if (!rows.length) return null;
  const sum = (key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
  return { count: rows.length, grossPayCents: sum("grossPayCents"), totalDeductionsCents: sum("totalDeductionsCents"), netPayCents: sum("netPayCents") };
}

/** The money of the period: net pay first; employer contributions are a business cost, not taken from pay. */
function PeriodMoney({ review, recorded }) {
  const employer = recorded ? 0 : review.members.reduce((sum, member) => sum + Number(member.statutory?.employerTotalCents || 0), 0);
  const tax = recorded ? 0 : review.members.reduce((sum, member) => sum + withheld(member), 0);
  const totals = recorded || review.totals;
  const figures = [
    ["Net pay", totals.netPayCents, true],
    ["Gross", totals.grossPayCents],
    ["Employee deductions", totals.totalDeductionsCents],
  ];
  if (employer) figures.push(["Employer contributions", employer]);
  const differs = recorded && ["netPayCents", "grossPayCents", "totalDeductionsCents"].some((key) => Number(recorded[key]) !== Number(review.totals[key] || 0));
  return (
    <section aria-label="Payroll totals" className="space-y-1">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:flex sm:flex-wrap sm:gap-x-10">
        {figures.map(([label, cents, strong]) => (
          <div key={label}>
            <dt className="text-meta text-secondary">{label}</dt>
            <dd><Money value={cents} className={strong ? "text-metric text-foreground" : "text-money text-foreground"} /></dd>
          </div>
        ))}
      </dl>
      <p className="max-w-[75ch] text-meta text-secondary">
        {recorded
          ? `From the ${recorded.count} payslip${recorded.count === 1 ? "" : "s"} of this payroll, as calculated.`
          : RECORD_STATUSES.includes(review.status)
            ? "Worked out from today's records; this payroll has no payslips."
            : review.calculated ? "From the current facts." : "A preview from the current facts; nothing is calculated yet."}
        {tax ? ` Employee deductions include ${formatMoney(tax)} withholding tax.` : ""}
        {employer ? " Employer contributions are paid by the business, not deducted from pay." : ""}
      </p>
      {differs ? (
        <div className="max-w-[75ch] pt-2 text-body-sm">
          <Status icon>
            Worked out again from today's records, net pay would be <Money value={review.totals.netPayCents} className="font-medium" />.
          </Status>
          <p className="ml-5 text-secondary">
            Records or pay rules changed after this payroll was calculated. The payslips are the record and are not changed; open an employee to see what differs.
          </p>
        </div>
      ) : null}
    </section>
  );
}

/**
 * PayrollInputsReview — one payroll period's workspace body: the current step and its action, what blocks sending
 * to Finance (each with the way out), the money, then each employee. `canManage` mirrors `hr:payroll:manage`; the
 * server enforces it. `payslips` are the period's stored result rows: once the payroll is with Finance or later they,
 * not today's recalculation, are the figures shown.
 */
export default function PayrollInputsReview({ periodId, period = null, payslips = null, onChanged, onOpenPeriod, onOpenEmployeePay, onStart, onNotice, canManage = true }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [failure, setFailure] = useState("");
  const [confirming, setConfirming] = useState(null); // "submit" | "release"
  // Which employee (and contribution or holiday day) a blocker asked to open: { memberId, agency, holidayDate, at }.
  const [focus, setFocus] = useState(null);
  // The stored holiday decisions of a calculated payroll (they carry the ids a review needs), by employee.
  const [storedHolidays, setStoredHolidays] = useState(null);
  const [reviewingHoliday, setReviewingHoliday] = useState(null); // { member, line }
  const [reviewingPremium, setReviewingPremium] = useState(null); // { member, line } (Report 55)
  // Finding employees in the payroll. Display only — every check above still covers everyone.
  const [query, setQuery] = useState("");
  const [show, setShow] = useState("all");
  const [order, setOrder] = useState("attention");
  const [limit, setLimit] = useState(LIST_STEP);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inputs, holidays] = await Promise.all([
        businessApi.getPayrollInputs(periodId),
        // If the stored decisions cannot be read, the review still shows the days from current facts (not reviewable).
        Promise.resolve().then(() => businessApi.getPayrollHolidayLines(periodId)).catch(() => null),
      ]);
      setReview(inputs);
      setStoredHolidays(Array.isArray(holidays?.lines) ? holidays.lines : null);
      setError("");
    } catch (err) {
      setError(actionError(err, "The payroll couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  const members = review?.members;
  // The payslips are the figures once the payroll is a record; null while it is still being prepared.
  const recorded = useMemo(
    () => (review && RECORD_STATUSES.includes(review.status) ? payslipTotals(review, payslips) : null),
    [review, payslips]
  );
  const record = Boolean(recorded);
  const listed = useMemo(() => {
    const text = query.trim().toLowerCase();
    // In a record nothing is left to resolve: the one thing worth finding is a payslip today's records no longer match.
    const stateOf = (member) => (record
      ? { attention: recordDiffers(member), changed: recordDiffers(member), rank: recordDiffers(member) ? 0 : 1 }
      : memberState(member));
    const netOf = (member) => Number((record ? member.stored?.netPayCents : member.netPayCents) || 0);
    const rows = (members || []).filter((member) => {
      const state = stateOf(member);
      if (text && !String(member.name || "").toLowerCase().includes(text)) return false;
      if (show === "attention") return state.attention;
      if (show === "changed") return state.changed;
      if (show === "clear") return !state.attention;
      return true;
    });
    const byName = (x, y) => String(x.name || "").localeCompare(String(y.name || "")) || Number(x.memberId) - Number(y.memberId);
    const sorters = {
      attention: (x, y) => stateOf(x).rank - stateOf(y).rank || byName(x, y),
      name: byName,
      net: (x, y) => netOf(y) - netOf(x) || byName(x, y),
    };
    return rows.sort(sorters[order] || sorters.attention);
  }, [members, query, show, order, record]);

  // A blocker that names an employee always finds them: filters are cleared if they hide the employee, and the list
  // grows far enough to show them.
  useEffect(() => {
    if (!focus?.memberId || !members) return;
    const hidden = !listed.some((member) => Number(member.memberId) === Number(focus.memberId));
    if (hidden) {
      setQuery("");
      setShow("all");
      return;
    }
    const index = listed.findIndex((member) => Number(member.memberId) === Number(focus.memberId));
    if (index >= limit) setLimit(index + 1);
  }, [focus, listed, members, limit]);

  const afterChange = useCallback(async () => {
    await load();
    await onChanged?.();
  }, [load, onChanged]);

  if (loading && !review) return <SkeletonRows rows={4} columns={3} label="Loading the payroll" />;
  if (error && !review) {
    return <ErrorState title="This payroll couldn't be loaded" description={error} onRetry={load} />;
  }

  const status = review.status;
  const attentionCount = record ? 0 : review.members.filter((member) => memberState(member).attention).length;
  const differingCount = record ? review.members.filter(recordDiffers).length : 0;
  // One employee needs no finding; a record with nothing differing has nothing to filter by.
  const showControls = review.members.length > 1;
  const showFilter = record ? differingCount > 0 : true;
  const showOrder = review.members.length > ORDER_CONTROL_FROM;
  const listFiltered = Boolean(query.trim()) || show !== "all" || order !== "attention";
  const clearList = () => { setQuery(""); setShow("all"); setOrder("attention"); setLimit(LIST_STEP); };
  const lastReview = review.period?.last_finance_review || period?.last_finance_review || null;
  const step = currentStep(status, lastReview);
  const StepIcon = STEP_ICON[step.tone];
  const preparing = ["collecting_inputs", "computed"].includes(status);
  const periodForDates = period || review.period;

  // Calculating replaces this period's results and decides what payroll pays (C3): it is confirmed first in
  // CalculatePayrollDialog, a refusal stays in that dialog, and afterwards anyone the calculation skipped is listed.
  const nameOf = (memberId) => review.members.find((m) => Number(m.memberId) === Number(memberId))?.name || "An employee";
  const calculate = () => {
    if (busy) return;
    setFailure("");
    setConfirming("calculate");
  };
  const runCalculation = async () => {
    const recalculated = status === "computed";
    const result = await businessApi.processPayrollPeriod(periodId);
    setConfirming(null);
    await afterChange();
    const skipped = Array.isArray(result?.exceptions) ? result.exceptions : [];
    onNotice?.({
      tone: skipped.length ? "warning" : "success",
      text: skipped.length
        ? `Payroll calculated. ${skipped.length} employee${skipped.length === 1 ? " was" : "s were"} skipped: ${skipped.map((row) => `${row.memberName || nameOf(row.memberId)} — ${row.message}`).join(" ")}`
        : `Payroll ${recalculated ? "recalculated" : "calculated"} from the current facts.`,
    });
  };

  // Opening a draft only lets HR work on it; nothing is calculated yet, so it runs without a confirmation.
  const start = async () => {
    if (busy) return;
    setBusy("open");
    setFailure("");
    try {
      await onStart();
      await load();
    } catch (err) {
      setFailure(actionError(err, "This payroll couldn't be started. Try again."));
    } finally {
      setBusy("");
    }
  };

  // A calculated payroll shows its stored holiday decisions; before that, the days worked out from current facts.
  const holidaysOf = (member) => {
    if (storedHolidays && review.calculated && status !== "collecting_inputs" && status !== "draft") {
      return storedHolidays.filter((line) => Number(line.memberId) === Number(member.memberId));
    }
    return member.holidays || [];
  };
  const anyHoliday = review.members.some((member) => holidaysOf(member).length > 0);

  const blockerActions = (item) => {
    if (!canManage) return [];
    if (item.code === "holiday_review_required") {
      return (item.items || []).map((row) => ({
        label: `Review ${row.name}, ${shortDate(new Date(`${row.date}T00:00:00`))}`,
        run: () => {
          setFocus({ memberId: row.memberId, agency: "", holidayDate: row.date, at: Date.now() });
          const line = status === "computed" && storedHolidays
            ? storedHolidays.find((stored) => Number(stored.memberId) === Number(row.memberId) && stored.date === row.date && stored.state === "REVIEW_REQUIRED")
            : null;
          const member = review.members.find((m) => Number(m.memberId) === Number(row.memberId));
          if (line && member) setReviewingHoliday({ member, line });
        },
      }));
    }
    if (item.code === "premium_review_required" && status === "computed") {
      return (item.items || []).map((row) => ({
        label: `Decide ${row.name}, ${shortDate(new Date(`${row.date}T00:00:00`))}`,
        run: () => {
          setFocus({ memberId: row.memberId, agency: "", at: Date.now() });
          const member = review.members.find((m) => Number(m.memberId) === Number(row.memberId));
          const line = member?.premiumLines?.find((l) => l.date === row.date && l.component === row.component);
          if (member && line) setReviewingPremium({ member, line });
        },
      }));
    }
    if (item.code === "results_stale" || item.code === "statutory_month_stale") return [{ label: "Recalculate", run: calculate }];
    if (item.code === "statutory_review_required" && status === "computed") {
      return review.members.flatMap((member) => (member.statutory?.lines || [])
        .filter((line) => line.state === "REVIEW_REQUIRED")
        .map((line) => ({
          label: `Review ${AGENCY_NAME[line.agency]} for ${member.name}`,
          run: () => setFocus({ memberId: member.memberId, agency: line.agency, at: Date.now() }),
        })));
    }
    if (item.code === "withholding_review_required" && status === "computed") {
      return review.members
        .filter((member) => member.statutory?.withholding?.state === "REVIEW_REQUIRED")
        .map((member) => ({
          label: `Review withholding tax for ${member.name}`,
          run: () => setFocus({ memberId: member.memberId, agency: "BIR_WHT", at: Date.now() }),
        }));
    }
    if (item.code === "statutory_month_open" && onOpenPeriod) {
      return (review.statutory?.openPeriods || []).map((row) => ({ label: `Open ${row.name || "the earlier payroll"}`, run: () => onOpenPeriod(row.id) }));
    }
    if (item.code === "employees_without_pay_schedule" && onOpenEmployeePay) {
      return (item.items || []).map((row) => ({ label: `Choose a pay schedule for ${row.name}`, run: () => onOpenEmployeePay(row.memberId) }));
    }
    if (item.code === "unresolved_inputs") {
      const first = review.members.find((member) => member.warnings.some((w) => w.level === "blocker" && !/^statutory_|changed_since_calculation|pay_change_inside_period/.test(w.code)));
      return first ? [{ label: `Show ${first.name}`, run: () => setFocus({ memberId: first.memberId, agency: "", at: Date.now() }) }] : [];
    }
    return [];
  };

  const stepActions = !canManage ? null : status === "draft" && onStart ? (
    <Button variant="primary" loading={busy === "open"} onClick={start}>Start preparing</Button>
  ) : status === "collecting_inputs" ? (
    <Button variant="primary" disabled={Boolean(busy)} onClick={calculate}>
      Calculate
    </Button>
  ) : status === "computed" ? (
    <>
      <Button icon={RotateCcw} disabled={Boolean(busy)} onClick={calculate}>Recalculate</Button>
      <Button variant="primary" disabled={Boolean(busy) || !review.canSubmit} onClick={() => setConfirming("submit")}>Send to Finance</Button>
    </>
  ) : status === "approved" ? (
    <Button variant="primary" onClick={() => setConfirming("release")}>Release payslips</Button>
  ) : null;

  return (
    <section className="space-y-6" aria-labelledby="payroll-inputs-title">
      <section
        aria-label="Next step"
        className={cn(
          "flex flex-col gap-3 rounded-panel border bg-surface p-4 sm:flex-row sm:items-center sm:justify-between",
          step.tone === "warning" ? "border-warning" : "border-default"
        )}
      >
        <div className="flex min-w-0 gap-3">
          {StepIcon ? <StepIcon aria-hidden="true" className={cn("mt-0.5 h-4 w-4 shrink-0", STEP_ICON_CLASS[step.tone])} /> : null}
          <div className="min-w-0">
            <p className="text-panel-title text-foreground">{step.title}</p>
            <p className="mt-0.5 text-body-sm text-secondary">{step.text}</p>
          </div>
        </div>
        {stepActions ? <div className="flex flex-col-reverse gap-2 sm:shrink-0 sm:flex-row">{stepActions}</div> : null}
      </section>

      {failure ? <Notice tone="danger" title="Not done" role="alert">{failure}</Notice> : null}

      {preparing && review.blockers.length ? (
        <section aria-label="Before this can go to Finance" className="rounded-panel border border-warning bg-warning-subtle">
          <h3 className="flex items-center gap-2 px-4 pt-3 text-panel-title text-foreground">
            <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
            Before this can go to Finance
          </h3>
          <ul className="divide-y divide-warning px-4 pb-1 text-body-sm">
            {review.blockers.map((item) => {
              const actions = blockerActions(item);
              return (
                <li key={item.code} className="flex flex-col gap-2 py-2.5">
                  <span className="text-foreground">{blockerMessage(item, periodForDates)}</span>
                  {actions.length ? (
                    <span className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                      {actions.slice(0, 3).map((action) => (
                        <Button key={action.label} size="sm" disabled={Boolean(busy)} onClick={action.run}>{action.label}</Button>
                      ))}
                      {actions.length > 3 ? <span className="self-center text-meta text-secondary">and {actions.length - 3} more in the list below</span> : null}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {preparing && review.blockers.some((item) => item.code === "period_not_ended") ? (
        <Notice title="Calculated so far">
          These figures count only the days that have ended. Days not due yet are never counted as absences; recalculate after the period ends before sending it to Finance.
        </Notice>
      ) : null}

      <PeriodMoney review={review} recorded={recorded} />

      {anyHoliday ? (
        <DisclosureLink label="How holiday pay was calculated" contentClassName="max-w-[75ch] space-y-2 pb-1 text-body-sm text-secondary">
          <p>Each holiday day is taken from the official calendar and the government's published holiday pay rules: a regular holiday, a special non-working day, whether it was worked, and whether it fell on the employee's rest day.</p>
          <p>{HOLIDAY_SCOPE_NOTE}</p>
        </DisclosureLink>
      ) : null}

      <section aria-labelledby="payroll-inputs-title" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="payroll-inputs-title" className="text-section-title text-foreground">Employees ({review.members.length})</h2>
          {attentionCount ? <p className="text-meta text-secondary">{attentionCount} need{attentionCount === 1 ? "s" : ""} attention</p> : null}
          {differingCount ? <p className="text-meta text-secondary">{differingCount} payslip{differingCount === 1 ? "" : "s"} today's records no longer match</p> : null}
        </div>
        {showControls ? (
          <Toolbar label="Find employees in this payroll" actions={listFiltered ? <Button size="sm" variant="quiet" onClick={clearList}>Clear</Button> : null}>
            <SearchInput label="Search employees in this payroll" placeholder="Search by name" className="w-full sm:w-64" value={query} onChange={(event) => { setQuery(event.target.value); setLimit(LIST_STEP); }} />
            {showFilter ? (
              <Select aria-label="Show employees" className="w-auto" value={show} onChange={(event) => { setShow(event.target.value); setLimit(LIST_STEP); }}>
                <option value="all">Everyone</option>
                {record ? null : <option value="attention">Needs attention</option>}
                <option value="changed">{record ? "Records changed since" : "Changed since calculation"}</option>
                <option value="clear">{record ? "Matches the payslip" : "Nothing to check"}</option>
              </Select>
            ) : null}
            {showOrder ? (
              <Select aria-label="Order" className="w-auto" value={order} onChange={(event) => setOrder(event.target.value)}>
                <option value="attention">{record ? "Changed records first" : "Needs attention first"}</option>
                <option value="name">Name A–Z</option>
                <option value="net">Net pay, highest first</option>
              </Select>
            ) : null}
            {listFiltered ? (
              <p role="status" className="text-meta tabular-nums text-secondary">
                {listed.length === review.members.length ? `${listed.length} employees` : `${listed.length} of ${review.members.length} employees`}
              </p>
            ) : <p role="status" className="sr-only">{review.members.length} employees</p>}
          </Toolbar>
        ) : null}
        {review.members.length && !listed.length ? (
          <div className="rounded-panel border border-default bg-surface px-4 py-6 text-center text-body-sm text-secondary">
            No employee matches. The checks above still cover everyone. <Button size="sm" variant="quiet" onClick={clearList}>Clear</Button>
          </div>
        ) : null}
        {listed.length ? (
          <div aria-hidden="true" className={cn("hidden gap-x-4 px-4 text-cell-head text-secondary xl:grid", ROW_COLUMNS)}>
            <span>Employee</span>
            <span>{record ? "Payslip" : "Contributions and tax"}</span>
            <span>{record ? "Since calculated" : "Review"}</span>
            <span className="text-right">Net pay</span>
            <span />
          </div>
        ) : null}
        {listed.length ? (
          <ul className="space-y-2" aria-label="Employees in this payroll">
            {listed.slice(0, limit).map((member) => (
              <EmployeeCard
                key={member.memberId}
                member={member}
                periodId={periodId}
                status={status}
                record={record}
                focus={focus}
                canManage={canManage}
                onChanged={afterChange}
                holidayLines={holidaysOf(member)}
                onReviewHoliday={(target, line) => setReviewingHoliday({ member: target, line })}
                onReviewPremium={(target, line) => setReviewingPremium({ member: target, line })}
              />
            ))}
          </ul>
        ) : null}
        {listed.length > limit ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => setLimit((value) => value + LIST_STEP)}>Show {Math.min(LIST_STEP, listed.length - limit)} more</Button>
            <span className="text-meta tabular-nums text-secondary">Showing {limit} of {listed.length}</span>
          </div>
        ) : null}
        {!review.members.length ? <p className="text-body-sm text-secondary">Nobody is employed in this period.</p> : null}
      </section>

      {reviewingHoliday ? (
        <HolidayReviewDialog
          periodId={periodId}
          memberName={reviewingHoliday.member.name}
          line={reviewingHoliday.line}
          onClose={() => setReviewingHoliday(null)}
          onSaved={async (view) => {
            setReviewingHoliday(null);
            await afterChange();
            onNotice?.({ tone: "success", text: `Holiday pay for ${view.title} (${view.dateText}) saved. The payroll was recalculated.` });
          }}
          onStale={async () => {
            setReviewingHoliday(null);
            await afterChange();
          }}
        />
      ) : null}
      {reviewingPremium ? (
        <PremiumReviewDialog
          periodId={periodId}
          member={reviewingPremium.member}
          line={reviewingPremium.line}
          onClose={() => setReviewingPremium(null)}
          onSaved={async (line) => {
            setReviewingPremium(null);
            await afterChange();
            onNotice?.({ tone: "success", text: `${PREMIUM_LABEL[line.component]} for ${reviewingPremium.member.name} saved. The payroll was recalculated.` });
          }}
          onStale={async () => {
            setReviewingPremium(null);
            await afterChange();
          }}
        />
      ) : null}
      {confirming === "calculate" ? (
        <CalculatePayrollDialog
          period={periodForDates}
          recalculate={status === "computed"}
          employeeCount={review.members.length}
          onConfirm={runCalculation}
          onClose={() => setConfirming(null)}
        />
      ) : null}
      {confirming === "submit" ? (
        <PeriodActionDialog
          title="Send this payroll to Finance?"
          description="Finance approves it or returns it to you. While Finance reviews, attendance, leave, overtime and pay for these dates are locked."
          confirmLabel="Send to Finance"
          failureTitle="Payroll can't be sent yet"
          onClose={() => setConfirming(null)}
          onConfirm={async () => {
            await businessApi.submitPayrollForReview(periodId);
            setConfirming(null);
            await afterChange();
            onNotice?.({ tone: "success", text: "Sent to Finance. It waits there for approval." });
          }}
        >
          <p>Net pay <Money value={review.totals.netPayCents} className="font-medium text-foreground" /> for {review.members.filter((m) => m.pay).length} employee(s).</p>
        </PeriodActionDialog>
      ) : null}
      {confirming === "release" ? (
        <PeriodActionDialog
          title="Release the payslips?"
          description="Employees can then see their payslips. Releasing pays nobody: Finance records each salary payment."
          confirmLabel="Release payslips"
          failureTitle="Payslips weren't released"
          onClose={() => setConfirming(null)}
          onConfirm={async () => {
            await businessApi.releasePayrollPeriod(periodId);
            setConfirming(null);
            await afterChange();
            onNotice?.({ tone: "success", text: "Payslips released. Finance now pays each salary." });
          }}
        />
      ) : null}
    </section>
  );
}
